import React, { useCallback, useState, useEffect, useMemo } from "react";
import { convertDollarToCents, formatDecimalInput } from "src/helpers";
import Button from "src/components/common/Button";
import FormRowVertical from "src/components/common/FormRowVertical";
import Input, { InputAsEnum, InputTypeEnum } from "src/components/common/Input";
import MenuItemLocaleFields from "src/components/common/MenuItemLocaleFields";
import { MenuItemOptionElementFormProps } from "src/interfaces/menu-items-props.interface";
import { useStorePosLocales } from "src/hooks/useStorePosLocales";
import {
  buildLocaleTranslationMap,
  buildTranslationsPayload,
  LocaleTranslationMap,
  syncLegacyNameFieldsFromLocaleTranslations,
} from "src/utils/localeTranslations";

const MenuItemOptionElementForm = React.memo(
  ({
    menuItemOptionElement,
    menuItemOptionElementIndex,
    menuItemOptionIndex,
    totalMenuItemOptionElements,
    formik,
    displayPrices,
    setDisplayPrices,
    isAddingMenuItemOptionElement,
    hasMenuItemOptionCorrelationId,
    onChangeElementValue,
    updateMenuItemOptionElement,
    removeMenuItemOptionElement,
    addNewElement,
    inlineMode = false,
    onCancel,
    onDoneEditingNewElement,
  }: MenuItemOptionElementFormProps) => {
    const { values, setFieldValue } = formik;
    const { enabledLocaleOptions, enabledLocales, defaultLocale } = useStorePosLocales();
    const [descriptionExpanded, setDescriptionExpanded] = useState(false);

    const fieldPath = `itemOptionInputList[${menuItemOptionIndex}].addItemOptionElementInputList[${menuItemOptionElementIndex}]`;

    const initialElementLocaleTranslations = useMemo(
      () =>
        menuItemOptionElement?.localeTranslations ??
        buildLocaleTranslationMap(menuItemOptionElement?.translations, enabledLocales, {
          englishName: menuItemOptionElement?.name,
          nonEnglishName: menuItemOptionElement?.nonEnglishName,
        }),
      [menuItemOptionElement, enabledLocales]
    );

    const [elementLocaleTranslations, setElementLocaleTranslations] =
      useState<LocaleTranslationMap>(initialElementLocaleTranslations);

    useEffect(() => {
      setElementLocaleTranslations(initialElementLocaleTranslations);
    }, [initialElementLocaleTranslations]);

    const syncElementLegacyFields = useCallback(
      (next: LocaleTranslationMap) => {
        const legacy = syncLegacyNameFieldsFromLocaleTranslations(next);
        const menuItemOptionList = structuredClone(values.itemOptionInputList ?? []);
        const menuItemOption = menuItemOptionList[menuItemOptionIndex];

        if (!menuItemOption) return;

        if (!menuItemOption.addItemOptionElementInputList) {
          menuItemOption.addItemOptionElementInputList = [];
        }

        if (!menuItemOption.addItemOptionElementInputList[menuItemOptionElementIndex]) {
          menuItemOption.addItemOptionElementInputList[menuItemOptionElementIndex] = {
            name: "",
            nonEnglishName: "",
            description: "",
            renderSequence: 1,
            priceCents: 0,
          };
        }

        const targetElement =
          menuItemOption.addItemOptionElementInputList[menuItemOptionElementIndex];

        targetElement.name = legacy.name;
        targetElement.nonEnglishName = legacy.nonEnglishName;
        targetElement.translations = buildTranslationsPayload(next, enabledLocales);
        targetElement.localeTranslations = next;

        setFieldValue("itemOptionInputList", menuItemOptionList);
      },
      [
        setFieldValue,
        values.itemOptionInputList,
        menuItemOptionIndex,
        enabledLocales,
        menuItemOptionElementIndex,
      ]
    );

    const handlePriceChange = useCallback(
      (
        e: React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      ) => {
        const value = e.target.value;
        const formattedValue = formatDecimalInput(value, 2);

        setDisplayPrices((prev) => ({
          ...prev,
          [`${menuItemOptionIndex}-${menuItemOptionElementIndex}`]: formattedValue,
        }));

        const centsValue = convertDollarToCents(formattedValue);
        const menuItemOptionList = [...(values.itemOptionInputList || [])];

        if (!menuItemOptionList[menuItemOptionIndex]) return;
        const menuItemOptionElements =
          menuItemOptionList[menuItemOptionIndex].addItemOptionElementInputList || [];
        if (!menuItemOptionElements[menuItemOptionElementIndex]) return;
        menuItemOptionElements[menuItemOptionElementIndex].priceCents = centsValue;
        menuItemOptionList[menuItemOptionIndex].addItemOptionElementInputList =
          menuItemOptionElements;
        setFieldValue("itemOptionInputList", menuItemOptionList);
      },
      [
        setDisplayPrices,
        menuItemOptionIndex,
        menuItemOptionElementIndex,
        setFieldValue,
        values.itemOptionInputList,
      ]
    );

    const handlePriceBlur = useCallback(() => {
      const currentValue =
        displayPrices[`${menuItemOptionIndex}-${menuItemOptionElementIndex}`];
      let formattedValue: string;
      if (!currentValue || currentValue.trim() === "") {
        formattedValue = "0.00";
      } else {
        const numericValue = parseFloat(currentValue);
        formattedValue = isNaN(numericValue) ? "0.00" : numericValue.toFixed(2);
      }

      setDisplayPrices((prev) => ({
        ...prev,
        [`${menuItemOptionIndex}-${menuItemOptionElementIndex}`]: formattedValue,
      }));

      const centsValue = convertDollarToCents(formattedValue);
      const menuItemOptionList = [...(values.itemOptionInputList || [])];
      if (!menuItemOptionList[menuItemOptionIndex]) return;
      const menuItemOptionElements =
        menuItemOptionList[menuItemOptionIndex].addItemOptionElementInputList || [];
      if (!menuItemOptionElements[menuItemOptionElementIndex]) return;
      menuItemOptionElements[menuItemOptionElementIndex].priceCents = centsValue;
      menuItemOptionList[menuItemOptionIndex].addItemOptionElementInputList =
        menuItemOptionElements;
      setFieldValue("itemOptionInputList", menuItemOptionList);
    }, [
      displayPrices,
      setDisplayPrices,
      menuItemOptionIndex,
      menuItemOptionElementIndex,
      setFieldValue,
      values.itemOptionInputList,
    ]);

    const nameError = formik.getFieldMeta(`${fieldPath}.name`).error;
    const priceError = formik.getFieldMeta(`${fieldPath}.priceCents`).error;
    const sequenceError = formik.getFieldMeta(`${fieldPath}.renderSequence`).error;

    const hasMenuItemOptionElementValue = !!(
      menuItemOptionElement.name ||
      menuItemOptionElement.renderSequence ||
      menuItemOptionElement.priceCents != null
    );

    const hasErrors = !!(nameError || priceError || sequenceError);
    const isUpdateDisabled = !hasMenuItemOptionElementValue || hasErrors;

    const handleRemoveClick = useCallback(() => {
      if (menuItemOptionElement?.correlationId) {
        updateMenuItemOptionElement(menuItemOptionElementIndex, true);
      } else {
        removeMenuItemOptionElement(menuItemOptionElementIndex);
      }
    }, [
      menuItemOptionElement?.correlationId,
      updateMenuItemOptionElement,
      menuItemOptionElementIndex,
      removeMenuItemOptionElement,
    ]);

    const handleUpdateClick = useCallback(() => {
      if (!menuItemOptionElement?.correlationId) {
        addNewElement(menuItemOptionElementIndex);
      } else {
        updateMenuItemOptionElement(menuItemOptionElementIndex);
      }
    }, [
      menuItemOptionElement?.correlationId,
      addNewElement,
      menuItemOptionElementIndex,
      updateMenuItemOptionElement,
    ]);

    const showDescriptionField =
      !inlineMode || descriptionExpanded || !!menuItemOptionElement?.description;

  const localeFields = (
      <MenuItemLocaleFields
        enabledLocaleOptions={enabledLocaleOptions}
        defaultLocale={defaultLocale}
        localeTranslations={elementLocaleTranslations}
        onChange={(next: LocaleTranslationMap) => {
          setElementLocaleTranslations(next);
          syncElementLegacyFields(next);
        }}
        entityLabel="Option item"
        showDescription={false}
        nameError={nameError}
      />
    );

    return (
      <div
        key={menuItemOptionElementIndex}
        className={
          inlineMode
            ? "relative mb-2 rounded border border-[#E5E7EB] bg-[#f9fafb] p-2.5 shadow-sm border-l-2 border-l-[#F97316]"
            : "relative border border-[#F1F1F2] p-4 mb-6 rounded-lg"
        }
      >
        {!inlineMode && totalMenuItemOptionElements !== 1 && (
          <Button
            variant="orange"
            size="sm"
            isLoading={false}
            disabled={false}
            onClick={handleRemoveClick}
            className="absolute right-[-10px] top-[-10px] !h-[20px] !w-[20px] !p-0 !min-w-0 text-xs"
          >
            -
          </Button>
        )}

        {inlineMode ? (
          <>
            <div className="grid grid-cols-5 gap-2">
              <div className="col-span-3">{localeFields}</div>
              <FormRowVertical label="Price" required compact>
                <Input
                  type={InputTypeEnum.TEXT}
                  className="w-full"
                  name={`itemOptionInputList[${menuItemOptionIndex}].addItemOptionElementInputList[${menuItemOptionElementIndex}].priceDisplay`}
                  value={
                    displayPrices[`${menuItemOptionIndex}-${menuItemOptionElementIndex}`] ?? ""
                  }
                  onChange={handlePriceChange}
                  onBlur={handlePriceBlur}
                  placeholder="0.00"
                />
              </FormRowVertical>
              <FormRowVertical label="Sequence" required compact>
                <Input
                  type={InputTypeEnum.NUMBER}
                  min="1"
                  className="w-full"
                  {...formik.getFieldProps(`${fieldPath}.renderSequence`)}
                  onChange={(e) => {
                    const value = e.target.value;
                    const numValue = value === "" ? null : Number(value) || null;
                    formik.setFieldValue(`${fieldPath}.renderSequence`, numValue);
                    onChangeElementValue("renderSequence", value, menuItemOptionElementIndex);
                  }}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    const value = e.target.value;
                    const numValue = value === "" || value == null ? null : Number(value);
                    if (numValue == null || !Number.isFinite(numValue) || numValue <= 0) {
                      const finalValue = 1;
                      formik.setFieldValue(`${fieldPath}.renderSequence`, finalValue);
                      onChangeElementValue(
                        "renderSequence",
                        String(finalValue),
                        menuItemOptionElementIndex
                      );
                    }
                  }}
                />
              </FormRowVertical>
            </div>

            {showDescriptionField ? (
              <FormRowVertical label="Description" compact className="mt-2">
                <Input
                  as={InputAsEnum.TEXTAREA}
                  className="min-h-[60px]"
                  name={`${fieldPath}.description`}
                  value={menuItemOptionElement?.description ?? ""}
                  onBlur={formik.handleBlur}
                  onChange={(e) =>
                    onChangeElementValue(
                      "description",
                      e.target.value,
                      menuItemOptionElementIndex
                    )
                  }
                />
              </FormRowVertical>
            ) : (
              <button
                type="button"
                onClick={() => setDescriptionExpanded(true)}
                className="mt-2 text-xs text-[#9CA3AF] hover:text-[#6b7280] hover:underline focus:outline-none"
              >
                + Add description (optional)
              </button>
            )}
          </>
        ) : (
          <>
            {localeFields}
            <div className="mt-3 flex gap-x-4">
              <FormRowVertical label="Show sequence" required={true}>
                <Input
                  type={InputTypeEnum.NUMBER}
                  min="1"
                  {...formik.getFieldProps(`${fieldPath}.renderSequence`)}
                  onChange={(e) => {
                    const value = e.target.value;
                    const numValue = value === "" ? null : Number(value) || null;
                    formik.setFieldValue(`${fieldPath}.renderSequence`, numValue);
                    onChangeElementValue("renderSequence", value, menuItemOptionElementIndex);
                  }}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    const value = e.target.value;
                    const numValue = value === "" || value == null ? null : Number(value);
                    if (numValue == null || !Number.isFinite(numValue) || numValue <= 0) {
                      const finalValue = 1;
                      formik.setFieldValue(`${fieldPath}.renderSequence`, finalValue);
                      onChangeElementValue(
                        "renderSequence",
                        String(finalValue),
                        menuItemOptionElementIndex
                      );
                    }
                  }}
                />
              </FormRowVertical>

              <FormRowVertical label="Price" required={true}>
                <Input
                  type={InputTypeEnum.TEXT}
                  name={`itemOptionInputList[${menuItemOptionIndex}].addItemOptionElementInputList[${menuItemOptionElementIndex}].priceDisplay`}
                  value={
                    displayPrices[`${menuItemOptionIndex}-${menuItemOptionElementIndex}`] ?? ""
                  }
                  onChange={handlePriceChange}
                  onBlur={handlePriceBlur}
                  placeholder="0.00"
                />
              </FormRowVertical>
            </div>

            <FormRowVertical label="Description" className="mt-2">
              <Input
                as={InputAsEnum.TEXTAREA}
                name={`${fieldPath}.description`}
                value={menuItemOptionElement?.description}
                onBlur={formik.handleBlur}
                onChange={(e) =>
                  onChangeElementValue(
                    "description",
                    e.target.value,
                    menuItemOptionElementIndex
                  )
                }
              />
            </FormRowVertical>
          </>
        )}

        {(hasMenuItemOptionCorrelationId || (inlineMode && onDoneEditingNewElement)) && (
          <div
            className={`flex justify-end gap-x-3 ${inlineMode ? "mt-3" : "mt-4"} flex-1 items-center`}
          >
            {inlineMode && onCancel && (
              <Button variant="ghost" onClick={onCancel}>
                Cancel
              </Button>
            )}
            {hasMenuItemOptionCorrelationId ? (
              <Button
                variant="secondary"
                isLoading={!menuItemOptionElement?.correlationId && isAddingMenuItemOptionElement}
                disabled={isUpdateDisabled}
                onClick={handleUpdateClick}
              >
                Save Option Item
              </Button>
            ) : (
              inlineMode &&
              onDoneEditingNewElement && (
                <Button variant="secondary" onClick={onDoneEditingNewElement}>
                  Done
                </Button>
              )
            )}
          </div>
        )}
      </div>
    );
  }
);

MenuItemOptionElementForm.displayName = "MenuItemOptionElementForm";

export default MenuItemOptionElementForm;
